import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import 'rxjs/add/observable/forkJoin';

// Config
import { Config } from '../../providers/config/config';

// Helpers
import { HelperService } from '../../providers/helpers/helper-service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json; charset=UTF-8;'
  })
}

@Injectable()
export class InstructorsService {
  data: any;

  constructor( 
    public http: HttpClient,
    public helperService: HelperService
  ) {}

  getInstructors()
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors' )
      .pipe( map( this.processData, this ));
  }

  getInstructor( instructorId )
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId )
      .pipe( map( this.processData, this ));
  }

  getInstructorByRegistryNumber( registryNumber )
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructor-lookup/' + registryNumber )
        .pipe( map(this.processData, this));
  }

  getInstructorCandidates()
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructor-candidates' )
        .pipe( map(this.processData, this));
  }

  getInstructorsApplicationStatusDateRange( dateStart, dateEnd )
  {
    // We need to execute multiple POST requests before
    // returning our observable.
    // https://bit.ly/2IzVc2I
    let urls = [];

    urls.push( this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors-status-date?status=expiring&date_start=' + dateStart + '&date_end=' + dateEnd ) );
    urls.push( this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors-status-date?status=new-certification&date_start=' + dateStart + '&date_end=' + dateEnd ) );
    urls.push( this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors-status-date?status=re-certification&date_start=' + dateStart + '&date_end=' + dateEnd ) );
    urls.push( this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors-status-date?status=rejected&date_start=' + dateStart + '&date_end=' + dateEnd ) );

    return Observable.forkJoin(urls);
  }

  getInstructorsByEmployer( providerId, status = null )
  {
    let url = Config.API_ROOT_INSTRUCTORS + 'providers/' + providerId + '/instructors/' 
    + status;
    if ( status === null )
    {
      url = Config.API_ROOT_INSTRUCTORS + 'providers/' + providerId + '/instructors';
    }
    return this.http.get( url )
        .pipe( map(this.processData, this));
  }

  getInstructorsByStatus( status )
  {
    status = encodeURI(status);

    // Look for Expiring instructors.
    if ( status === 'Expiring' )
    {
      return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors-expiring?date_expire=' + this.helperService.getLastDayOfMonth() )
        .pipe( map(this.processData, this));

    // I don't think we need these statusses anymore.
    } else if ( status === 'Active' || status === 'Inactive' ) { 
      let isActive = 'Yes';
      if ( status === 'Inactive' ) { isActive = 'No'; }

      return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors?is_active=' + isActive )
        .pipe( map(this.processData, this));    

    // Look for status = Instructor Candidate.
    // 05/16/20: Add application_status=Pending to this call since I 
    // think we're only using it on the dashboard and we want Pending Instructor Candidates.
    } else if ( decodeURIComponent(status) === 'Instructor Candidate' ) {
      //return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors?status=' + status + '&application_status=Pending' )

      return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors?application_status=Pending' )
        .pipe( map(this.processData, this));
    
    // Look for all other application_status values (Approve, Pending, Rejected, etc.).
    } else {
      return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors?application_status=' + status )
        .pipe( map(this.processData, this));
    }
  }

  getInstructorsUncertifiedCandidates()
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors?status=Pass&application_status=Pending')
        .pipe( map(this.processData, this));
  }

  filterInstructors( providerId, statusText )
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'filter/' + providerId + '/' + statusText )
        .pipe( map(this.processData, this));
  }

  searchInstructors( search_term )
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors?search=' + search_term )
      .pipe( map( this.processData, this ));
  }

  saveInstructor( instructor )
  {
    if ( instructor.instructor_id > 0 )
    {
      return this.http.put( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructor.instructor_id, JSON.stringify(instructor), httpOptions );
    } else {
      return this.http.post( Config.API_ROOT_INSTRUCTORS + 'instructors', JSON.stringify(instructor), httpOptions );
    }
  }

  approveInstructorCandidate( instructor ) {
    return this.http.put( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructor.instructor_id + '?approve-instructor-candidate=Yes', JSON.stringify(instructor), httpOptions );
  }

  approveInstructorRecertification( instructor ) {
    return this.http.put( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructor.instructor_id + '?approve-instructor-recertification=Yes', JSON.stringify(instructor), httpOptions );
  }

  deleteInstructor( instructorId )
  {
    return this.http.delete( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId );
  }

  addProviderToInstructor( data )
  {
    return this.http.post( Config.API_ROOT_INSTRUCTORS + 'instructors/' + data.instructor_id + '/employers', JSON.stringify(data), httpOptions );
  }

  removeProviderFromInstructor( instructorId, itemId )
  {
    return this.http.delete( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId + '/employers/' + itemId );
  }

  saveCertification( certification )
  {
    if ( certification.certification_id > 0 )
    {
      return this.http.put( Config.API_ROOT_INSTRUCTORS + 'instructors/' + certification.instructor_id + '/certifications/' + certification.certification_id, JSON.stringify(certification), httpOptions );
    } else {
      return this.http.post( Config.API_ROOT_INSTRUCTORS + 'instructors/' + certification.instructor_id + '/certifications', JSON.stringify(certification), httpOptions );
    }
  }

  deleteCertification( instructorId, itemId )
  {
    return this.http.delete( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId + '/certifications/' + itemId );
  }

  saveCourse( course )
  {
    if ( course.course_id > 0 )
    {
      return this.http.put( Config.API_ROOT_INSTRUCTORS + 'instructors/' + course.instructor_id + '/courses/' + course.course_id, JSON.stringify(course), httpOptions );
    } else {
      return this.http.post( Config.API_ROOT_INSTRUCTORS + 'instructors/' + course.instructor_id + '/courses', JSON.stringify(course), httpOptions );
    }
  }

  deleteCourse( instructorId, itemId )
  {
    return this.http.delete( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId + '/courses/' + itemId );
  }

  saveEducation( instructors, education )
  {
    if ( education.education_id > 0 )
    {
      // Edit condition
      return this.http.put( Config.API_ROOT_INSTRUCTORS + 'instructors/' + education.instructor_id + '/education/' + education.education_id, JSON.stringify(education), httpOptions );
    } else {
      // New record condition
      // We need to execute multiple POST requests before
      // returning our observable.
      // https://bit.ly/2IzVc2I
      let urls = [];
      for ( let instructor of instructors )
      {
        education.instructor_id = instructor.instructor_id;
        urls.push(this.http.post( Config.API_ROOT_INSTRUCTORS + 'instructors/' + education.instructor_id + '/education', JSON.stringify(education), httpOptions ));
      }
      return Observable.forkJoin(urls);
    }
  }

  deleteEducation( instructorId, itemId )
  {
    return this.http.delete( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId + '/education/' + itemId );
  }

  saveIncident( incident )
  {
    if ( incident.incident_id > 0 )
    {
      return this.http.put( Config.API_ROOT_INSTRUCTORS + 'instructors/' + incident.instructor_id + '/incidents/' + incident.incident_id, JSON.stringify(incident), httpOptions );
    } else {
      return this.http.post( Config.API_ROOT_INSTRUCTORS + 'instructors/' + incident.instructor_id + '/incidents', JSON.stringify(incident), httpOptions );
    }
  }

  deleteIncident( instructorId, itemId )
  {
    return this.http.delete( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId + '/incidents/' + itemId );
  }

  sendPortalLink( instructorId )
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId + '/send-portal-email' );
  }

  getPortalLink( instructorId, registryNumber, guid )
  {
    let unique = registryNumber;
    console.log(registryNumber + " " + guid)
    if ( registryNumber < 1 )
    {
      unique = guid + '&guid=true';
    }
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'instructors/' + instructorId + '/get-portal-accessurl?unique=' + unique );
  }

  sendPassCourseCertificate( instructorId )
  {
    return this.http.get( Config.API_ROOT_INSTRUCTORS + 'send-pass-course-certificate-email/' + instructorId);
  }

  processData(data: any) 
  {
    this.data = data;

    if ( typeof this.data.length === 'undefined' )
    {
      this.data = this.setApplicationStatusColor( this.data );
      this.data = this.setActiveStatusColor( this.data );
    } else {
      let newData = [];
      this.data.forEach((item) => {
        item = this.setApplicationStatusColor( item );
        item = this.setActiveStatusColor( item );
        item = this.determineCertExpiration( item );
        item.selected = false;
        newData.push( item );    
      });
      this.data = newData;
    }
    return this.data;
  } 

  determineCertExpiration( item )
  {
    let expDate = '';
    if ( item.certifications !== null && item.certifications[0] )
    {
      expDate = item.certifications[0].date_expire;
    }
    item.date_cert_exp = expDate;

    return item;
  }

  setApplicationStatusColor( item )
  {
    switch ( item.application_status )
    {
      case 'Pending':
        item.ApplicationStatusColor = 'light-gray';
        break;
      case 'Approved':
        item.ApplicationStatusColor = 'success';
        break;
      case 'Rejected':
        item.ApplicationStatusColor = 'warning';
        break;  
      case 'Expired':
        item.ApplicationStatusColor = 'danger';
        break;
      default:
        item.ApplicationStatusColor = 'light-gray';
    }

    return item;
  }

  setActiveStatusColor( item )
  {
    switch ( item.is_active )
    {
      case 'Yes':
        item.ActiveStatusColor = 'success';
        break;
      case 'No':
        item.ActiveStatusColor = 'light-gray';
        break;
      default:
        item.ActiveStatusColor = 'light-gray';
    }

    return item;
  }
}  


// WEBPACK FOOTER //
// ./src/providers/instructors/instructors-service.ts